import React, { useEffect, useRef, useState } from 'react';
import {
  Box, TextField, Popover, List, ListItemButton, Checkbox, Typography,
  InputAdornment, IconButton, Button, Divider, CircularProgress,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';

/**
 * Filtro de uma coluna. Dois comportamentos, decididos pelo backend:
 *
 *   faceta.tipo === 'lista'  → dropdown com os valores que a coluna tem e
 *     quantas linhas cada um cobre. Até 40 valores distintos vêm todos; acima
 *     disso vêm os 10 mais frequentes e digitar busca o resto no servidor.
 *     Seleção múltipla — marcar 'drop' e 'reject' juntos é o caso comum.
 *
 *   faceta.tipo === 'texto'  → campo de busca puro. Enter aplica. É o que
 *     sobra pra coluna cujos valores são quase todos únicos (descrição, nome
 *     de interface, IP): oferecer "os 10 mais frequentes" ali seria ocupar a
 *     tela com 10 valores que aparecem uma vez cada.
 */
export default function ColumnFilter({ col, label, faceta, valor, onChange, onBuscarFaceta }) {
  const [anchor, setAnchor] = useState(null);
  const [busca, setBusca] = useState('');
  const [opcoes, setOpcoes] = useState(faceta?.topo || []);
  const [carregando, setCarregando] = useState(false);
  const [texto, setTexto] = useState(valor?.op === 'contains' ? valor.value : '');
  const debounce = useRef(null);

  const ehLista = faceta?.tipo === 'lista';
  const marcados = valor?.op === 'in' ? valor.value : [];

  useEffect(() => { setOpcoes(faceta?.topo || []); }, [faceta]);
  useEffect(() => {
    setTexto(valor?.op === 'contains' ? valor.value : '');
  }, [valor]);

  // Busca no servidor só quando a coluna tem mais valores do que veio no topo.
  useEffect(() => {
    if (!anchor || !ehLista) return undefined;
    if (!busca && (faceta?.distintos || 0) <= (faceta?.topo || []).length) return undefined;
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      setCarregando(true);
      onBuscarFaceta(col, busca)
        .then((vs) => setOpcoes(vs))
        .finally(() => setCarregando(false));
    }, 250);
    return () => debounce.current && clearTimeout(debounce.current);
  }, [busca, anchor, col, ehLista, faceta, onBuscarFaceta]);

  const alterna = (v) => {
    const novo = marcados.includes(v)
      ? marcados.filter((x) => x !== v)
      : [...marcados, v];
    onChange(novo.length ? { col, op: 'in', value: novo } : null);
  };

  const ativo = !!valor;

  if (!ehLista) {
    return (
      <TextField
        size="small"
        variant="standard"
        placeholder="buscar…"
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        onKeyDown={(e) => {
          // Enter aplica. Não filtra a cada tecla de propósito: cada mudança
          // é uma ida ao servidor, e busca em coluna de texto livre costuma
          // ser digitada inteira antes de valer alguma coisa.
          if (e.key === 'Enter') {
            onChange(texto ? { col, op: 'contains', value: texto } : null);
          }
          if (e.key === 'Escape') { setTexto(''); onChange(null); }
        }}
        onBlur={() => {
          if ((texto || '') !== (valor?.value || '')) {
            onChange(texto ? { col, op: 'contains', value: texto } : null);
          }
        }}
        InputProps={{
          disableUnderline: false,
          sx: { fontSize: 11 },
          endAdornment: texto ? (
            <InputAdornment position="end">
              <IconButton size="small" sx={{ p: 0.2 }}
                          onClick={() => { setTexto(''); onChange(null); }}>
                <ClearIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        sx={{ width: '100%', minWidth: 80 }}
      />
    );
  }

  return (
    <>
      <Button
        size="small"
        onClick={(e) => setAnchor(e.currentTarget)}
        endIcon={<FilterListIcon sx={{ fontSize: 13 }} />}
        sx={{
          fontSize: 11, textTransform: 'none', py: 0, px: 0.5,
          justifyContent: 'space-between', width: '100%', minWidth: 80,
          color: ativo ? '#b91c1c' : '#64748b',
          fontWeight: ativo ? 700 : 400,
        }}
      >
        {ativo ? `${marcados.length} sel.` : 'todos'}
      </Button>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 300, maxHeight: 420 } } }}
      >
        <Box sx={{ p: 1, pb: 0.5 }}>
          <Typography sx={{ fontSize: 11, color: '#64748b', mb: 0.5 }}>
            {label} · {faceta.distintos} valores
          </Typography>
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder="buscar valor…"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            InputProps={{ sx: { fontSize: 12 } }}
          />
        </Box>
        <Divider />
        <List dense sx={{ maxHeight: 260, overflowY: 'auto', py: 0 }}>
          {carregando && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
              <CircularProgress size={16} />
            </Box>
          )}
          {!carregando && !opcoes.length && (
            <Typography sx={{ fontSize: 12, color: '#94a3b8', p: 1 }}>
              nenhum valor
            </Typography>
          )}
          {!carregando && opcoes.map((o) => (
            <ListItemButton
              key={o.v}
              dense
              onClick={() => alterna(o.v)}
              sx={{ py: 0, px: 0.5 }}
            >
              <Checkbox size="small" checked={marcados.includes(o.v)} sx={{ p: 0.4 }} />
              <Typography sx={{
                fontSize: 12, flex: 1, overflow: 'hidden',
                textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {o.v}
              </Typography>
              {/* a contagem é o que torna a escolha informada: "drop (906)"
                  diz muito mais que "drop" */}
              <Typography sx={{ fontSize: 11, color: '#94a3b8', ml: 0.5 }}>
                {o.n}
              </Typography>
            </ListItemButton>
          ))}
        </List>
        <Divider />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 0.5 }}>
          <Button size="small" sx={{ fontSize: 11 }} onClick={() => onChange(null)}>
            limpar
          </Button>
          <Button size="small" sx={{ fontSize: 11 }} onClick={() => setAnchor(null)}>
            fechar
          </Button>
        </Box>
      </Popover>
    </>
  );
}
